import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminViewModeSwitch = () => {
  const { user, viewMode, setViewMode, subscription, refreshSubscription } = useAuth();
  const [plans, setPlans] = useState([]);
  const [simulatedPlan, setSimulatedPlan] = useState('');

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/plans`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Backend returns array directly, not wrapped in object
        const plansData = Array.isArray(response.data) ? response.data : (response.data.plans || []);
        console.log('📋 AdminViewModeSwitch - Plans loaded:', plansData.length, plansData);
        setPlans(plansData);
        
        // Set current plan as default
        if (subscription?.plan_id) {
          console.log('📋 Setting simulatedPlan from subscription:', subscription.plan_id);
          setSimulatedPlan(subscription.plan_id);
        } else if (plansData.length > 0) {
          // Default to first plan if no subscription
          console.log('📋 Setting simulatedPlan to first plan:', plansData[0].id);
          setSimulatedPlan(plansData[0].id);
        }
      } catch (error) {
        console.error('❌ Failed to fetch plans:', error);
      }
    };
    
    if (user?.is_admin) {
      fetchPlans();
    }
  }, [user?.is_admin, subscription?.plan_id]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('viewMode', mode);
  };

  const handlePlanChange = async (planId) => {
    setSimulatedPlan(planId);
    localStorage.setItem('simulatedPlan', planId);
    
    // Apply simulated plan features without page reload
    if (planId) {
      const selectedPlan = plans.find(p => p.id === planId);
      if (selectedPlan) {
        // Store simulated features
        localStorage.setItem('simulatedFeatures', JSON.stringify(selectedPlan.features));
        // Refresh subscription to apply the new simulated features
        await refreshSubscription();
        console.log('✅ Plan simuliert:', selectedPlan.name);
      }
    }
  };

  // Only show for admins
  if (!user?.is_admin) return null;

  return (
    <div className="px-3 py-3 border-b" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4" style={{ color: '#7CD4FD' }} />
          <span className="text-xs font-semibold" style={{ color: '#7CD4FD' }}>
            Testmodus
          </span>
        </div>
        
        {/* Compact Toggle Switch */}
        <div className="flex items-center rounded-full p-1" style={{ backgroundColor: '#1F2937' }}>
          <button
            onClick={() => handleViewModeChange('admin')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
              viewMode === 'admin' ? 'shadow-md' : ''
            }`}
            style={{
              backgroundColor: viewMode === 'admin' ? '#1EA0E6' : 'transparent',
              color: viewMode === 'admin' ? '#FFFFFF' : '#94A3B8'
            }}
          >
            <Shield className="w-3.5 h-3.5" />
            Admin
          </button>
          <button
            onClick={() => handleViewModeChange('user')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
              viewMode === 'user' ? 'shadow-md' : ''
            }`}
            style={{
              backgroundColor: viewMode === 'user' ? '#1EA0E6' : 'transparent',
              color: viewMode === 'user' ? '#FFFFFF' : '#94A3B8'
            }}
          >
            <Eye className="w-3.5 h-3.5" />
            User
          </button>
        </div>
      </div>

      {/* Plan Simulation (only in User mode) - Compact Design */}
      {viewMode === 'user' && (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: '#1F2937' }}>
          {plans.length === 0 ? (
            <div className="text-xs" style={{ color: '#94A3B8' }}>
              Pläne werden geladen...
            </div>
          ) : (
            <Select value={simulatedPlan} onValueChange={handlePlanChange}>
              <SelectTrigger 
                className="w-full h-8 text-xs" 
                style={{ 
                  backgroundColor: '#1F2937', 
                  borderColor: '#374151', 
                  color: '#E6EDF3'
                }}
              >
                <div className="flex items-center gap-2">
                  <span style={{ color: '#94A3B8' }}>Plan:</span>
                  <SelectValue placeholder="Wählen..." />
                </div>
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
                {plans.map(plan => (
                  <SelectItem 
                    key={plan.id} 
                    value={plan.id}
                    className="text-xs"
                    style={{ color: '#E6EDF3' }}
                  >
                    {plan.name} - {plan.price > 0 ? `${plan.price / 100}€` : 'Kostenlos'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Compact Status Indicator */}
      <div 
        className="mt-2 px-2 py-1 rounded text-xs flex items-center gap-1.5" 
        style={{ 
          backgroundColor: viewMode === 'admin' ? '#10B98120' : '#F59E0B20',
          color: viewMode === 'admin' ? '#10B981' : '#F59E0B'
        }}
      >
        <div 
          className="w-1.5 h-1.5 rounded-full" 
          style={{ 
            backgroundColor: viewMode === 'admin' ? '#10B981' : '#F59E0B'
          }}
        />
        {viewMode === 'admin' ? (
          <span>Vollzugriff aktiv</span>
        ) : (
          <span>Plan-Features simuliert</span>
        )}
      </div>
    </div>
  );
};
